import type { CompareId, HubId, MarketingPageId, UseCaseId } from './types';
import { COMPARE_IDS, USE_CASE_IDS } from './types';

/** Language-independent parts of the marketing pages: addresses, pictures, code, links. */

export const HUB_PATHS: Record<HubId, string> = {
  'use-cases': '/use-cases',
  compare: '/compare',
};

export const PAGE_PATHS: Record<MarketingPageId, string> = {
  'product-cards': '/use-cases/product-cards',
  'catalog-ads': '/use-cases/catalog-ads',
  'ad-creatives': '/use-cases/ad-creatives',
  'social-media': '/use-cases/social-media',
  'google-sheets': '/use-cases/google-sheets',
  'image-api': '/use-cases/image-generation-api',
  'og-images': '/use-cases/dynamic-og-images',
  certificates: '/use-cases/certificates',
  'manual-vs-automated': '/compare/manual-vs-automated',
  'design-to-production': '/compare/design-to-production',
  'image-api-checklist': '/compare/image-api-checklist',
};

export const hubOf = (id: MarketingPageId): HubId =>
  (USE_CASE_IDS as readonly string[]).includes(id) ? 'use-cases' : 'compare';

export const HUB_PAGES: Record<HubId, readonly MarketingPageId[]> = {
  'use-cases': USE_CASE_IDS,
  compare: COMPARE_IDS,
};

/**
 * The picture under the first screen: one template filled with the page's sample rows
 * (`rows`, fields = layer names in the order of `samples`), or one template in several sizes.
 * `template` is a gallery template id or "certificate" (built in visuals.tsx).
 */
export type Visual =
  | { kind: 'rows'; template: string; fields: string[] }
  | { kind: 'sizes'; template: string; sizes: Array<[number, number]> };

export const VISUALS: Record<MarketingPageId, Visual> = {
  'product-cards': {
    kind: 'rows',
    template: 'new-collection',
    fields: ['title', 'subtitle', 'price'],
  },
  'catalog-ads': {
    kind: 'rows',
    template: 'headphones-promo',
    fields: ['title', 'price', 'badge'],
  },
  'ad-creatives': {
    kind: 'sizes',
    template: 'sale-landscape',
    sizes: [
      [1200, 628],
      [1080, 1080],
      [300, 250],
      [300, 600],
      [1080, 1920],
    ],
  },
  'social-media': {
    kind: 'sizes',
    template: 'announcement',
    sizes: [
      [1080, 1080],
      [1080, 1350],
      [1080, 1920],
      [1200, 628],
    ],
  },
  'google-sheets': { kind: 'rows', template: 'summer-sale', fields: ['title', 'price', 'badge'] },
  'image-api': { kind: 'rows', template: 'headphones-promo', fields: ['title', 'price', 'badge'] },
  'og-images': { kind: 'rows', template: 'online-course', fields: ['kicker', 'title'] },
  certificates: { kind: 'rows', template: 'certificate', fields: ['name', 'course'] },
  'manual-vs-automated': { kind: 'rows', template: 'flash-sale', fields: ['title', 'badge'] },
  'design-to-production': {
    kind: 'sizes',
    template: 'travel-banner',
    sizes: [
      [1200, 628],
      [1080, 1080],
      [1080, 1920],
      [300, 600],
    ],
  },
  'image-api-checklist': {
    kind: 'rows',
    template: 'restaurant',
    fields: ['title', 'price', 'badge'],
  },
};

/** Code samples (the same in every language). */
export const CODE: Partial<Record<MarketingPageId, { language: string; code: string }>> = {
  'image-api': {
    language: 'request',
    code: `curl -X POST "https://api.bannermatic.app/v1/renders?wait=true" \\
  -H "Authorization: Bearer bm_live_…" \\
  -H "Content-Type: application/json" \\
  -d '{
    "template_id": "tpl_promo",
    "format": "png",
    "layers": {
      "title": "Studio Pro headphones",
      "price": { "text": "$129", "color": "#FACC15" },
      "photo": "https://cdn.shop.com/p/123.jpg",
      "badge": null
    }
  }'`,
  },
  'og-images': {
    language: 'html',
    code: `<meta property="og:image"
  content="https://api.bannermatic.app/i/tpl_blog.png?title=How%20we%20cut%20costs&kicker=CASE%20STUDY&s=…" />`,
  },
  'catalog-ads': {
    language: 'csv',
    code: `id,title,price,badge,image_link
sku-101,Studio Pro headphones,$129,-20%,https://api.bannermatic.app/i/tpl_ad.png?title=…&price=%24129&badge=-20%25&s=…
sku-102,Air Runner 2,$89,NEW,https://api.bannermatic.app/i/tpl_ad.png?title=…&price=%2489&badge=NEW&s=…`,
  },
};

/** Pages linked under "Related" (internal links between use cases and comparisons). */
export const RELATED: Record<MarketingPageId, MarketingPageId[]> = {
  'product-cards': ['catalog-ads', 'google-sheets', 'ad-creatives'],
  'catalog-ads': ['product-cards', 'ad-creatives', 'image-api'],
  'ad-creatives': ['social-media', 'catalog-ads', 'design-to-production'],
  'social-media': ['ad-creatives', 'google-sheets', 'manual-vs-automated'],
  'google-sheets': ['product-cards', 'certificates', 'manual-vs-automated'],
  'image-api': ['og-images', 'catalog-ads', 'image-api-checklist'],
  'og-images': ['image-api', 'social-media', 'image-api-checklist'],
  certificates: ['google-sheets', 'image-api', 'manual-vs-automated'],
  'manual-vs-automated': ['design-to-production', 'google-sheets', 'product-cards'],
  'design-to-production': ['manual-vs-automated', 'ad-creatives', 'social-media'],
  'image-api-checklist': ['image-api', 'og-images', 'google-sheets'],
};

export const isUseCase = (id: MarketingPageId): id is UseCaseId => hubOf(id) === 'use-cases';
export const isComparison = (id: MarketingPageId): id is CompareId => hubOf(id) === 'compare';
